/**
 * The board's tools, down its left side like Miro's: select, hand, frame, then the drawing tools
 * whose kind the environment brings (Art: note, text, shape, image; UI/UX: its elements),
 * arrow (`[data-board-tool=<id>]`, the chosen one `aria-pressed`). Each says its key. Editors
 * only: a viewer gets select and hand. Where the board makes screens (UI/UX), Design and
 * Prototype modes (`[data-board-mode]`) and Play (`#board-play`) come last.
 */
import { For, Show, type JSX } from "solid-js";
import { useApp } from "../../../app/context";
import { PEN_COLOURS, PEN_WIDTHS, type BoardTool } from "../../../state/board";

/** The pen's colours and widths (`#board-pen`, `[data-pen-colour]`, `[data-pen-width]`), beside the tools while the pen is chosen. */
function PenOptions(): JSX.Element {
  const { board } = useApp();
  return (
    <div id="board-pen" class="board-pen" role="group" aria-label="Pen">
      <For each={PEN_COLOURS}>
        {(colour) => (
          <button type="button" class="board-pen-colour" data-pen-colour={colour} aria-pressed={board.pen().colour === colour} title={colour} style={{ background: colour }} onClick={() => board.setPen({ colour })} />
        )}
      </For>
      <span class="board-tools-sep" />
      <For each={PEN_WIDTHS}>
        {(width) => (
          <button type="button" data-pen-width={width} aria-pressed={board.pen().width === width} title={`${width} px`} onClick={() => board.setPen({ width })}>
            <span class="board-pen-dot" style={{ width: `${Math.min(14, width + 2)}px`, height: `${Math.min(14, width + 2)}px`, background: board.pen().colour }} />
          </button>
        )}
      </For>
    </div>
  );
}

/** A tool's little picture. */
function ToolIcon(props: { tool: BoardTool }): JSX.Element {
  const paths: Record<BoardTool, JSX.Element> = {
    select: <path d="M3 2l9 5-4 1.2L6.5 13z" />,
    hand: <path d="M5 8V3.5a1 1 0 012 0V7m0-4a1 1 0 012 0v4m0-3a1 1 0 012 0v5c0 3-2 5-4.5 5S3 12 3 10V7.5a1 1 0 012 0" />,
    note: <path d="M2.5 2.5h11v7l-4 4h-7zM13.5 9.5h-4v4" />,
    text: <path d="M3 3.5h10M8 3.5V13M6 13h4" />,
    shape: (
      <>
        <rect x="2" y="2" width="7" height="7" />
        <circle cx="10.5" cy="10.5" r="3.5" />
      </>
    ),
    arrow: <path d="M3 13L13 3M7 3h6v6" />,
    frame: <path d="M4 1.5v13M12 1.5v13M1.5 4h13M1.5 12h13" />,
    image: (
      <>
        <rect x="1.5" y="2.5" width="13" height="11" rx="1" />
        <path d="M1.5 11l4-4 3 3 2-2 4 4" />
        <circle cx="11" cy="5.5" r="1.2" />
      </>
    ),
    button: (
      <>
        <rect x="1.5" y="4.5" width="13" height="7" rx="3" />
        <path d="M5.5 8h5" />
      </>
    ),
    input: (
      <>
        <rect x="1.5" y="4.5" width="13" height="7" rx="1" />
        <path d="M4.5 6.5v3" />
      </>
    ),
    checkbox: (
      <>
        <rect x="2" y="3" width="10" height="10" rx="1.5" />
        <path d="M4.5 8l2 2 3.5-4" />
      </>
    ),
    list: <path d="M5.5 4h8M5.5 8h8M5.5 12h8M2.5 4h.5M2.5 8h.5M2.5 12h.5" />,
    pen: <path d="M11 2.5l2.5 2.5-7.5 7.5L3 13.5l1-3zM2 15c2-1 4 .5 6-.5" />,
    eraser: <path d="M9.5 2.5l4 4-6.5 6.5H4l-2-2zM6 6l4 4M7 13h7" />,
  };
  return (
    <svg viewBox="0 0 16 16" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round" aria-hidden="true">
      {paths[props.tool]}
    </svg>
  );
}

/** The tool column. */
export function ToolBar(): JSX.Element {
  const { board, access, commands } = useApp();
  const tools = () => (access.can("editor") ? board.tools() : board.tools().filter((t) => t.id === "select" || t.id === "hand"));
  return (
    <div class="board-tools" onPointerDown={(e) => e.stopPropagation()} onDblClick={(e) => e.stopPropagation()} role="toolbar" aria-label="Board tools" aria-orientation="vertical">
      <For each={tools()}>
        {(t) => (
          <button type="button" data-board-tool={t.id} aria-pressed={board.tool() === t.id} aria-label={t.label} title={`${t.label} (${t.key})`} onClick={() => board.setTool(t.id)}>
            <ToolIcon tool={t.id} />
          </button>
        )}
      </For>
      <Show when={board.prototypes()}>
        <span class="board-tools-sep" />
        <button type="button" data-board-mode="design" aria-pressed={!board.prototyping()} title="Design: draw and arrange (Shift+E switches)" onClick={() => board.setPrototyping(false)}>
          <svg viewBox="0 0 16 16" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round" aria-hidden="true">
            <path d="M11 2.5l2.5 2.5-8 8H3v-2.5z" />
          </svg>
        </button>
        <button type="button" data-board-mode="prototype" aria-pressed={board.prototyping()} title="Prototype: where each click leads (Shift+E switches)" onClick={() => board.setPrototyping(true)}>
          <svg viewBox="0 0 16 16" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round" aria-hidden="true">
            <circle cx="4" cy="4" r="2" />
            <path d="M5.5 5.5C8 8 9 10 13 12M13 12h-3.5M13 12V8.5" />
          </svg>
        </button>
        <button type="button" id="board-play" title="Play the prototype (Shift+P)" aria-label="Play the prototype" onClick={() => commands.run("prototype.play")}>
          <svg viewBox="0 0 16 16" width="18" height="18" fill="currentColor" aria-hidden="true">
            <path d="M4.5 2.5v11l9-5.5z" />
          </svg>
        </button>
      </Show>
      <Show when={board.tool() === "pen"}>
        <PenOptions />
      </Show>
    </div>
  );
}
